import { Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionReveal from "@/components/SectionReveal";
import WaveDivider from "@/components/WaveDivider";
import CTAStrip from "@/components/CTAStrip";

const faqs = [
  {
    question: "What types of flooring does JTB Floors install?",
    answer: "We install hardwood, ceramic, laminate, hard tile, and marble flooring. Hardwood is our specialty — we've been installing it for over 35 years.",
  },
  {
    question: "Do you provide materials or do I need to buy my own?",
    answer: "We handle everything. We buy all the materials for you, deliver them, and install them. You get a finished floor — materials and labor included.",
  },
  {
    question: "Do you work on both residential and commercial projects?",
    answer: "Yes. JTB Floors serves both residential and commercial clients with the same attention to detail.",
  },
  {
    question: "What areas do you serve?",
    answer: "We're based in Fox River Grove, IL and serve Barrington and surrounding areas within a 35-mile radius.",
  },
  {
    question: "Do you offer free estimates?",
    answer: "Yes. You can request a free estimate by calling us at (773) 934-2004 or filling out the form on our Contact page.",
  },
  {
    question: "What is your satisfaction guarantee?",
    answer: "Every project comes with a 100% satisfaction guarantee on all labor. If you're not happy with the work, we'll make it right — no questions asked.",
  },
  {
    question: "Do you repair water-damaged floors?",
    answer: "Yes. We'll repair your flooring even if the water damage wasn't caused by our work. That's how much we stand behind our craft.",
  },
  {
    question: "Are you BBB accredited?",
    answer: "Yes. JTB Floors is BBB accredited. We also carry liability insurance, available upon request.",
  },
  {
    question: "What are your business hours?",
    answer: "We're available Monday through Friday, 8 AM to 6 PM. Saturday and Sunday we are closed.",
  },
  {
    question: "How can I contact you?",
    answer: "You can call us at (773) 934-2004, use the form on our Contact page, or visit us at 2313 Evergreen Ave, Fox River Grove, IL 60021.",
  },
];

export const faqLd = {
  type: "application/ld+json",
  children: JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((q) => ({ "@type": "Question", name: q.question, acceptedAnswer: { "@type": "Answer", text: q.answer } })),
  }),
};

const FAQ = () => {
  return (
    <Layout>
      <SEOHead
        title="FAQ | JTB Floors — Barrington, IL"
        description="Frequently asked questions about JTB Floors services, guarantee, service area, and more. Serving Barrington, IL and surrounding areas."
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h1 className="text-4xl md:text-5xl font-heading font-black mb-4">Frequently Asked Questions</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Everything you need to know about working with JTB Floors.
            </p>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* FAQ Accordion */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <SectionReveal>
            <h2 className="sr-only">Common Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-base font-heading font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </SectionReveal>

          <SectionReveal delay={0.2}>
            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-4">Still have questions?</p>
              <Button asChild className="btn-shine gap-2">
                <a href="tel:17739342004">
                  <Phone className="w-4 h-4" /> Call (773) 934-2004
                </a>
              </Button>
            </div>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--secondary))" />

      <CTAStrip />
    </Layout>
  );
};

export default FAQ;
